import { Auth } from '@aws-amplify/auth';
import {
  dataResidencyManager,
  SupportedDataResidency,
} from './DataResidencyManager';
import { initializeAmplifyForDataResidency } from './DataResidencyAwareAuthHelpers';

interface LoginClues {
  ipDataResidency: SupportedDataResidency;
  storedDataResidency?: SupportedDataResidency;
  cachedDataResidency?: SupportedDataResidency;
  emailDomain?: string;
  browserLanguage?: string;
  timeZone?: string;
}

interface UserDataResidencyCache {
  email: string;
  dataResidency: SupportedDataResidency;
  lastSuccess: Date;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

interface AuthenticationResult {
  user: any;
  dataResidency: SupportedDataResidency;
  attemptCount: number;
  cluesUsed: LoginClues;
}

class DataResidencyCache {
  private static CACHE_KEY = 'peekapak.userDataResidencyCache';
  // NO EXPIRY - users don't migrate between regions

  static getCache(): UserDataResidencyCache[] {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch (error) {
      console.warn('Failed to load data residency cache:', error);
      return [];
    }
  }

  static cacheSuccessfulLogin(
    email: string,
    dataResidency: SupportedDataResidency,
  ): void {
    try {
      const cache = this.getCache().filter((c) => c.email !== email);

      cache.push({
        email,
        dataResidency,
        lastSuccess: new Date(),
        confidence: 'HIGH',
      });

      // No expiry filtering - keep all entries indefinitely
      // Users don't migrate between regions
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('Failed to cache successful login:', error);
    }
  }

  static getCachedDataResidency(email: string): SupportedDataResidency | null {
    try {
      const cache = this.getCache();
      const userCache = cache.find((c) => c.email === email);

      // Return cached value regardless of age - users don't migrate
      return userCache ? userCache.dataResidency : null;
    } catch (error) {
      console.warn('Failed to get cached data residency:', error);
      return null;
    }
  }

  static clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      console.warn('Failed to clear data residency cache:', error);
    }
  }

  static clearUserCache(email: string): void {
    try {
      const cache = this.getCache().filter((c) => c.email !== email);
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('Failed to clear user cache:', error);
    }
  }
}

/**
 * Analyze multiple signals to predict the most likely data residency for a user
 */
async function predictUserDataResidency(email: string): Promise<LoginClues> {
  const clues: LoginClues = {
    ipDataResidency: SupportedDataResidency.US, // Default fallback
  };

  // 1. Check cached successful data residency (strongest indicator)
  const cachedDataResidency = DataResidencyCache.getCachedDataResidency(email);
  if (cachedDataResidency) {
    clues.cachedDataResidency = cachedDataResidency;
  }

  // 2. Check localStorage stored data residency
  const storedDataResidency = localStorage.getItem(
    'peekapak.userDataResidency',
  ) as SupportedDataResidency;
  if (
    storedDataResidency &&
    Object.values(SupportedDataResidency).includes(storedDataResidency)
  ) {
    clues.storedDataResidency = storedDataResidency;
  }

  // 3. Get IP-based data residency detection
  try {
    clues.ipDataResidency =
      await dataResidencyManager.detectUserDataResidencyFromIP();
  } catch (error) {
    console.warn(
      'IP data residency detection failed, using US default:',
      error,
    );
    clues.ipDataResidency = SupportedDataResidency.US;
  }

  // 4. Analyze email domain for UAE indicators
  const emailLower = email.toLowerCase();
  if (
    emailLower.endsWith('.ae') ||
    emailLower.includes('dubai') ||
    emailLower.includes('abudhabi') ||
    emailLower.includes('sharjah') ||
    emailLower.includes('ajman') ||
    emailLower.includes('emirates') ||
    emailLower.includes('uae')
  ) {
    clues.emailDomain = 'uae-indicator';
  }

  // 5. Check browser language preference
  try {
    const lang =
      navigator.language || (navigator.languages && navigator.languages[0]);
    if (lang === 'ar-AE' || lang === 'en-AE') {
      clues.browserLanguage = 'uae-indicator';
    }
  } catch (error) {
    // Browser doesn't support navigator.language
  }

  // 6. Check timezone
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (
      timeZone === 'Asia/Dubai' ||
      timeZone === 'Asia/Muscat' ||
      timeZone === 'Asia/Qatar' ||
      timeZone === 'Asia/Kuwait' ||
      timeZone === 'Asia/Bahrain' ||
      timeZone === 'Asia/Riyadh'
    ) {
      clues.timeZone = 'uae-indicator';
    }
  } catch (error) {
    // Browser doesn't support Intl.DateTimeFormat
  }

  return clues;
}

/**
 * Score and order data residency options based on available clues
 */
function getDataResidencyAttemptOrder(
  clues: LoginClues,
): SupportedDataResidency[] {
  const scores = {
    [SupportedDataResidency.US]: 0,
    [SupportedDataResidency.UAE]: 0,
  };

  // Cached successful data residency gets highest priority (+10 points)
  // Since users don't migrate, this is extremely reliable
  if (clues.cachedDataResidency) {
    scores[clues.cachedDataResidency] += 10;
  }

  // Stored data residency preference gets high priority (+5 points)
  if (clues.storedDataResidency) {
    scores[clues.storedDataResidency] += 5;
  }

  // IP-based data residency gets medium priority (+3 points)
  scores[clues.ipDataResidency] += 3;

  // Email domain hints get +2 points
  if (clues.emailDomain === 'uae-indicator') {
    scores[SupportedDataResidency.UAE] += 2;
  }

  // Browser language gets +1 point
  if (clues.browserLanguage === 'uae-indicator') {
    scores[SupportedDataResidency.UAE] += 1;
  }

  // Timezone gets +1 point
  if (clues.timeZone === 'uae-indicator') {
    scores[SupportedDataResidency.UAE] += 1;
  }

  // Return data residency options ordered by score (highest first)
  return Object.entries(scores)
    .sort(([, a], [, b]) => b - a)
    .map(([dataResidency]) => dataResidency as SupportedDataResidency);
}

/**
 * Intelligent multi-data-residency sign in with fallback
 */
export async function intelligentDataResidencySignIn(
  username: string,
  password: string,
): Promise<AuthenticationResult> {
  const clues = await predictUserDataResidency(username);
  const dataResidencyOrder = getDataResidencyAttemptOrder(clues);

  console.log(`🎯 Login attempt order based on clues:`, dataResidencyOrder);
  console.log(`🔍 Clues used:`, clues);

  let lastError: Error | null = null;

  for (let i = 0; i < dataResidencyOrder.length; i++) {
    const dataResidency = dataResidencyOrder[i];

    try {
      console.log(
        `🔐 Attempting sign in: ${dataResidency.toUpperCase()} (attempt ${
          i + 1
        }/${dataResidencyOrder.length})`,
      );

      // Configure Amplify for this data residency (which determines AWS region)
      await initializeAmplifyForDataResidency(dataResidency);

      // Attempt authentication
      const user = await Auth.signIn(username, password);

      // SUCCESS: Cache the successful data residency and update app state
      DataResidencyCache.cacheSuccessfulLogin(username, dataResidency);
      await dataResidencyManager.setDataResidency(dataResidency);
      // Note: setDataResidency now automatically calls refreshEndpoints()

      console.log(`✅ Sign in successful: ${dataResidency.toUpperCase()}`);

      return {
        user,
        dataResidency,
        attemptCount: i + 1,
        cluesUsed: clues,
      };
    } catch (error) {
      lastError = error as Error;
      console.log(
        `❌ Sign in failed for ${dataResidency.toUpperCase()}:`,
        lastError.message,
      );

      // If this is the last data residency to try, we'll throw the error after the loop
      if (i === dataResidencyOrder.length - 1) {
        break;
      }

      // Continue to next data residency
      continue;
    }
  }

  // All data residency options failed - throw the last error with additional context
  const enhancedError = new Error(
    `Authentication failed in all data residency options. Last error: ${
      lastError?.message || 'Unknown error'
    }`,
  );
  (enhancedError as any).originalError = lastError;
  (enhancedError as any).dataResidenciesAttempted = dataResidencyOrder;
  (enhancedError as any).cluesUsed = clues;

  throw enhancedError;
}

/**
 * Smart sign up that uses data residency prediction
 */
export async function intelligentDataResidencySignUp(
  username: string,
  password: string,
  attributes: any,
): Promise<AuthenticationResult> {
  const clues = await predictUserDataResidency(username);
  const dataResidencyOrder = getDataResidencyAttemptOrder(clues);

  // For sign up, we use the highest-scored data residency
  const targetDataResidency = dataResidencyOrder[0];

  console.log(
    `📝 Creating new account in ${targetDataResidency.toUpperCase()} data residency based on clues:`,
    clues,
  );

  try {
    // Configure Amplify for target data residency (which determines AWS region)
    await initializeAmplifyForDataResidency(targetDataResidency);

    // Add data residency metadata to user attributes
    const enhancedAttributes = {
      ...attributes,
      'custom:dataResidency': targetDataResidency,
    };

    // Perform sign up
    const result = await Auth.signUp({
      username,
      password,
      attributes: enhancedAttributes,
    });

    // Cache the data residency for this user (indefinitely)
    DataResidencyCache.cacheSuccessfulLogin(username, targetDataResidency);
    await dataResidencyManager.setDataResidency(targetDataResidency);
    // Note: setDataResidency now automatically calls refreshEndpoints()

    console.log(
      `✅ Sign up successful in ${targetDataResidency.toUpperCase()} data residency`,
    );

    return {
      user: result,
      dataResidency: targetDataResidency,
      attemptCount: 1,
      cluesUsed: clues,
    };
  } catch (error) {
    console.error(
      `❌ Sign up failed in ${targetDataResidency.toUpperCase()} data residency:`,
      error,
    );
    throw error;
  }
}

/**
 * Utility function to get data residency prediction without authentication
 */
export async function predictOptimalDataResidency(email: string): Promise<{
  dataResidency: SupportedDataResidency;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  clues: LoginClues;
}> {
  const clues = await predictUserDataResidency(email);
  const dataResidencyOrder = getDataResidencyAttemptOrder(clues);
  const topDataResidency = dataResidencyOrder[0];

  // Calculate confidence based on clues
  let confidence: 'HIGH' | 'MEDIUM' | 'LOW' = 'LOW';

  if (clues.cachedDataResidency) {
    // Cached data is most reliable since users don't migrate
    confidence = 'HIGH';
  } else if (clues.storedDataResidency || clues.emailDomain) {
    confidence = 'MEDIUM';
  } else {
    confidence = 'LOW';
  }

  return {
    dataResidency: topDataResidency,
    confidence,
    clues,
  };
}

// Export utility class
export { DataResidencyCache };

// Export types for other modules to use
export type { LoginClues, UserDataResidencyCache, AuthenticationResult };

// Legacy exports for backward compatibility
export { intelligentDataResidencySignIn as intelligentRegionSignIn };
export { intelligentDataResidencySignUp as intelligentRegionSignUp };
export { predictOptimalDataResidency as predictOptimalRegion };
export { DataResidencyCache as RegionCache };
export type { UserDataResidencyCache as UserRegionCache };

/**
 * Intelligent session reactivation with multi-data-residency support
 * Attempts to reactivate user session by trying multiple regions based on clues
 */
export async function intelligentDataResidencyReactivation(
  email?: string,
): Promise<AuthenticationResult> {
  // Try to get email from various sources if not provided
  if (!email) {
    // First check localStorage for last logged in user (doesn't require Amplify to be configured)
    const lastUser = localStorage.getItem('peekapak.lastLoggedInUser');
    if (lastUser) {
      email = lastUser;
    }

    // Only try to get from current session if Amplify might be configured
    if (!email) {
      try {
        // First ensure we have some basic configuration
        const storedDataResidency = localStorage.getItem(
          'peekapak.userDataResidency',
        ) as SupportedDataResidency;
        if (
          storedDataResidency &&
          Object.values(SupportedDataResidency).includes(storedDataResidency)
        ) {
          // Initialize for stored residency first to avoid config errors
          await initializeAmplifyForDataResidency(storedDataResidency);
          // Now try to get the current user
          try {
            const currentUser = await Auth.currentAuthenticatedUser();
            email = currentUser?.attributes?.email || currentUser?.username;
          } catch {
            // User not authenticated or session expired
          }
        }
      } catch {
        // Couldn't initialize or get user
      }
    }
  }

  // If we still don't have an email, we can't use prediction clues effectively
  // but we can still try based on stored data residency and IP
  const clues = email
    ? await predictUserDataResidency(email)
    : {
        ipDataResidency: await dataResidencyManager
          .detectUserDataResidencyFromIP()
          .catch(() => SupportedDataResidency.US),
        storedDataResidency: localStorage.getItem(
          'peekapak.userDataResidency',
        ) as SupportedDataResidency,
      };

  const dataResidencyOrder = getDataResidencyAttemptOrder(clues);

  console.log(
    `🔄 Reactivation attempt order based on clues:`,
    dataResidencyOrder,
  );
  console.log(`🔍 Clues used for reactivation:`, clues);

  let lastError: Error | null = null;

  for (let i = 0; i < dataResidencyOrder.length; i++) {
    const dataResidency = dataResidencyOrder[i];

    try {
      console.log(
        `🔄 Attempting session reactivation: ${dataResidency.toUpperCase()} (attempt ${
          i + 1
        }/${dataResidencyOrder.length})`,
      );

      // Configure Amplify for this data residency
      await initializeAmplifyForDataResidency(dataResidency);

      // Attempt to get current session
      const session = await Auth.currentSession();
      const cognitoUser = await Auth.currentAuthenticatedUser();

      // Refresh the session
      await new Promise<void>((resolve, reject) => {
        cognitoUser.refreshSession(session.getRefreshToken(), (err: any) => {
          if (err) {
            reject(err);
          } else {
            resolve();
          }
        });
      });

      // SUCCESS: Cache the successful data residency
      if (email) {
        DataResidencyCache.cacheSuccessfulLogin(email, dataResidency);
      }
      await dataResidencyManager.setDataResidency(dataResidency);
      // Note: setDataResidency now automatically calls refreshEndpoints()

      // Store the email for future reactivations
      if (cognitoUser?.attributes?.email) {
        localStorage.setItem(
          'peekapak.lastLoggedInUser',
          cognitoUser.attributes.email,
        );
      }

      console.log(
        `✅ Session reactivation successful: ${dataResidency.toUpperCase()}`,
      );

      return {
        user: cognitoUser,
        dataResidency,
        attemptCount: i + 1,
        cluesUsed: clues,
      };
    } catch (error) {
      lastError = error as Error;
      console.log(
        `❌ Session reactivation failed for ${dataResidency.toUpperCase()}:`,
        lastError.message,
      );

      // If this is the last data residency to try, we'll throw the error after the loop
      if (i === dataResidencyOrder.length - 1) {
        break;
      }

      // Continue to next data residency
      continue;
    }
  }

  // All data residency options failed - throw the last error with additional context
  const enhancedError = new Error(
    `Session reactivation failed in all data residency options. Last error: ${
      lastError?.message || 'Unknown error'
    }`,
  );
  (enhancedError as any).originalError = lastError;
  (enhancedError as any).dataResidenciesAttempted = dataResidencyOrder;
  (enhancedError as any).cluesUsed = clues;

  throw enhancedError;
}
